import React, { useRef } from "react";
import { Select as BaseSelect, SelectProps, SelectRootSlotProps } from "@mui/base/Select";
import { Option } from "@mui/base/Option";
import "./LeafSelector.scss";
import { IconArrowUp } from "../../icons/IconArrowUp/IconArrowUp";
import { IconArrowDropdown } from "../../icons/IconArrowDropdown/IconArrowDropdown";
import { IconDropdownCarrot } from "../../icons/IconDropdownCarrot/IconDropdownCarrot";
import { IconSubsection } from "../../icons/IconSubsection/IconSubsection";

export type OptionType = {
  id: string;
  label: string;
  selected?: boolean;
  disabled?: boolean;
  value?: string;
};

export type Props = {
  options: OptionType[];
  onSelected: (option: OptionType) => void;
  ariaLabel?: string;
  disabled?: boolean;
  theme?: "basic" | "modern" | "blue";
  placeholder?: string;
  overridePrefixIcon?: (open: boolean) => React.ReactNode;
  overrideSuffixIcon?: (open: boolean) => React.ReactNode;
};

const LeafSelector = ({
  onSelected,
  options,
  ariaLabel,
  theme = "basic",
  placeholder,
  overridePrefixIcon,
  overrideSuffixIcon,
}: Props) => {
  const dropdownOptionRef = useRef<HTMLDivElement>(null);
  const toggleButtonRef = useRef<HTMLButtonElement>(null);
  const selectedOption = options.find((option) => option.selected) || null;
  const isPlaceholderEmpty = !placeholder || placeholder.trim().length === 0;

  const handleOptionClick = (option: OptionType) => {
    onSelected(option);
    toggleButtonRef.current?.focus({ preventScroll: true });
  };

  const Button = React.forwardRef(function Button<TValue extends NonNullable<unknown>, Multiple extends boolean>(
    props: SelectRootSlotProps<TValue, Multiple>,
    ref: React.Ref<HTMLButtonElement>,
  ) {
    const { ownerState, ...other } = props;

    const renderPrefixIcon = () => {
      if (typeof overridePrefixIcon === "function") {
        return overridePrefixIcon(ownerState.open);
      }

      if (theme === "modern") {
        return <IconSubsection />;
      }

      if (theme === "blue") return null;

      return null;
    };

    const prefixIcon = renderPrefixIcon();

    const renderSuffixIcon = () => {
      if (typeof overrideSuffixIcon === "function") {
        return overrideSuffixIcon(ownerState.open);
      }

      if (theme === "modern") {
        return <IconDropdownCarrot color="blue" open={ownerState.open} />;
      }

      if (theme === "blue") {
        return ownerState.open ? (
          <IconArrowUp backgroundClass="black" size="md" />
        ) : (
          <IconArrowDropdown backgroundClass="black" size="md" />
        );
      }

      return ownerState.open ? <IconArrowUp backgroundClass="blue" /> : <IconArrowDropdown backgroundClass="black" />;
    };

    return (
      <button
        {...other}
        ref={ref}
        aria-label={ariaLabel}
        type="button"
        className={`leaf-selector__dropdown-toggle leaf-selector__dropdown-toggle--${theme}${
          ownerState.open ? " leaf-selector__dropdown-toggle--open" : ""
        }
        }`}
      >
        <div className="leaf-selector__text">
          {placeholder && <div className="leaf-selector__placeholder">{placeholder}</div>}
          <div className="leaf-selector__selected-row">
            {prefixIcon && (
              <span className={`leaf-selector__prefix-icon leaf-selector__prefix-icon--${theme}`}>
                {renderPrefixIcon()}
              </span>
            )}

            <span className={`leaf-selector__label leaf-selector__${isPlaceholderEmpty ? "" : "has-placeholder"}`}>
              {other.children}
            </span>
          </div>
        </div>
        <div className={`leaf-selector__suffix-icon leaf-selector__suffix-icon--${theme}`}>{renderSuffixIcon()}</div>
      </button>
    );
  }) as <TValue extends NonNullable<unknown>, Multiple extends boolean>(
    props: SelectRootSlotProps<TValue, Multiple> & { ref?: React.Ref<HTMLButtonElement> },
  ) => React.JSX.Element;

  // Re-order only for blue theme
  const renderedOptions =
    theme === "blue"
      ? [...options].sort((a, b) => {
          if (a.selected) return -1;
          if (b.selected) return 1;
          return 0;
        })
      : options;

  const Select = React.forwardRef(function CustomSelect<TValue extends NonNullable<unknown>, Multiple extends boolean>(
    props: SelectProps<TValue, Multiple>,
    ref: React.Ref<HTMLButtonElement>,
  ) {
    const slots: SelectProps<TValue, Multiple>["slots"] = {
      root: Button,
      ...props.slots,
    };

    return <BaseSelect {...props} ref={ref} slots={slots} />;
  }) as <TValue extends NonNullable<unknown>, Multiple extends boolean>(
    props: SelectProps<TValue, Multiple> & { ref?: React.Ref<HTMLButtonElement> },
  ) => React.JSX.Element;

  return (
    <Select
      value={selectedOption?.label}
      key={ariaLabel}
      slotProps={{
        root: {
          ref: toggleButtonRef,
          role: "button",
          "aria-haspopup": "listbox",
        },
        listbox: {
          className: `leaf-selector__dropdown-options leaf-selector__dropdown-options--${theme}`,
          role: "listbox",
        },
        popup: {
          disablePortal: true,
          ref: dropdownOptionRef,
          className: `leaf-selector__dropdown-menu leaf-selector__dropdown-menu--${theme}`,
        },
      }}
    >
      {renderedOptions.map((option) => (
        <Option
          key={option.id}
          value={option.label}
          onClick={() => !option.disabled && handleOptionClick(option)}
          className={option.disabled ? "disabled" : option === selectedOption ? "selected" : ""}
          disabled={option.disabled}
        >
          {option.label}
        </Option>
      ))}
    </Select>
  );
};

export default LeafSelector;
